import './styles.scss';

import React, { useMemo } from 'react';

import { Location } from '@/models/gen/graphql';
import LocationsTableHeader from './LocationsTableHeader';
import LocationsTableRow from './LocationsTableRow';
import Table from '@/common/Table';

export type LocationsTableProps = {
  data: Location[];
  selected: string[];
  onSelect: (id: string | string[]) => void;
  onEdit: () => void;
  fetchMore?: () => void;
};

/**
 * Locations table component.
 * Wraps `Table` with a column header and row renderer specific to the location entity.
 * Sorting is handled via Zustand state and drives a BE re-query in the parent page.
 */
const LocationsTable = ({ data, selected, onSelect, onEdit, fetchMore }: LocationsTableProps): React.JSX.Element => {
  const Header = useMemo(
    (): React.JSX.Element => <LocationsTableHeader data={data} selected={selected} onSelect={onSelect} />,
    [data, selected, onSelect]
  );

  return (
    <Table className="LocationsTable" header={Header} showControls onEnd={fetchMore}>
      {data.map(
        (row: Location): React.JSX.Element => (
          <LocationsTableRow
            key={row.id}
            data={row}
            isSelected={selected.includes(row.id)}
            onSelect={onSelect as (id: string) => void}
            onDoubleClick={(): void => {
              onSelect([row.id]);
              onEdit();
            }}
          />
        )
      )}
    </Table>
  );
};

export default React.memo(LocationsTable);
